import React, {useEffect, createContext, useContext, useRef} from 'react'
import PropTypes from 'prop-types'
import Section from '../../../../components/section'
import {Box} from '@chakra-ui/react3'

// Create context to track if we're inside a Container and animation state
const StackContext = createContext({isInContainer: false, noAnimation: true})

function Container({children, noAnimation = false}) {
    return (
        <StackContext.Provider value={{isInContainer: true, noAnimation}}>
            <Section position="relative">
                {React.Children.map(children, (child) => {
                    // Check if child is a valid React element and of type Item
                    if (!React.isValidElement(child) || child.type !== Item) {
                        throw new Error(
                            'Stack.Container only accepts Stack.Item as children. ' +
                                `Received: ${child?.type?.name || typeof child}. ` +
                                'Please use Stack.Item components as direct children of Stack.Container.'
                        )
                    }
                    return child
                })}
            </Section>
        </StackContext.Provider>
    )
}

Container.propTypes = {
    children: PropTypes.node.isRequired,
    noAnimation: PropTypes.bool
}

function Item({children}) {
    const {isInContainer, noAnimation} = useContext(StackContext)
    const containerRef = useRef(null)

    if (!isInContainer) {
        throw new Error(
            'Stack.Item must be used as a child of Stack.Container. ' +
                'Please wrap your Stack.Item components with Stack.Container.'
        )
    }

    useEffect(() => {
        if (!containerRef.current || noAnimation) return

        const containerElement = containerRef.current

        function updateTopProperty() {
            const containerRect = containerElement.getBoundingClientRect()
            const stickyTopOffset = Math.min(window.innerHeight - containerRect.height, 0)
            containerElement.style.top = `${stickyTopOffset}px`
        }

        // resize observer to react properly to container changes, this is useful whenever our
        // content has element like images which can break the top value for the sticky behavior
        const resizeObserver = new ResizeObserver(() => {
            updateTopProperty()
        })

        resizeObserver.observe(containerElement)

        return () => {
            resizeObserver.disconnect()
        }
    }, [noAnimation])

    return (
        <Box position={noAnimation ? 'static' : 'sticky'} ref={containerRef}>
            {children}
        </Box>
    )
}

Item.propTypes = {
    children: PropTypes.node.isRequired
}

const Stack = {
    Container,
    Item
}

export default Stack
